import {
  isMediaCardString,
  keyToMediaCardType,
  type MediaCardString,
  type MediaCardType,
} from '@wandb/weave/common/types/media';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {useCallback, useMemo} from 'react';

import {useActiveHistoryKeyInfo} from '../../state/panelGenHistoryKeys/useActiveHistoryKeyInfo';
import {getMediaKeys} from './utils/getMediaKeys';

export const useGetMediaCardTypes = (
  mediaKeys: string[] | undefined
): (() => MediaCardType[] | undefined) => {
  const {historyKeyInfo: keyInfo} = useActiveHistoryKeyInfo();
  return useCallback(() => {
    const {keyTypes} = getMediaKeys(keyInfo);
    const mediaCardTypes = mediaKeys
      ?.map(key => (key != null ? keyTypes[key] : null))
      ?.filter(
        (mediaString): mediaString is MediaCardString =>
          isNotNullOrUndefined(mediaString) && isMediaCardString(mediaString)
      )
      ?.map(keyToMediaCardType);
    return mediaCardTypes;
  }, [mediaKeys, keyInfo]);
};

export const useMediaCardTypes = (mediaKeys: string[] | undefined) => {
  const getMediaCardTypes = useGetMediaCardTypes(mediaKeys);
  return useMemo(() => getMediaCardTypes(), [getMediaCardTypes]);
};

export const useMediaKeyToTypeMap = (
  mediaKeys: string[] | undefined
): Array<{key: string; mediaType: MediaCardType}> => {
  const {historyKeyInfo: keyInfo} = useActiveHistoryKeyInfo();
  const {keyTypes} = getMediaKeys(keyInfo);
  return useMemo(() => {
    const result: Array<{key: string; mediaType: MediaCardType}> = [];
    mediaKeys?.forEach(key => {
      const keyType = keyTypes[key];
      if (key && keyType && isMediaCardString(keyType)) {
        result.push({key, mediaType: keyToMediaCardType(keyType)});
      }
    });
    return result;
  }, [mediaKeys, keyTypes]);
};
